import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError, UnauthorizedError, documentsApi } from '@/shared/api'
import type { CatalogDocumentDTO } from '@/shared/api'
import { useAuth } from '@/features/auth'
import { scopeEquals, scopeFromDocument, useScope } from '@/features/scope'
import { Icon } from '@/shared/ui/Icon'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { DocumentList } from './DocumentList'
import { DocumentUploadPanel } from './DocumentUploadPanel'
import { IngestionJobsPanel } from './IngestionJobsPanel'
import { documentTitle } from './documentUtils'
import {
  filterDocuments,
  sortDocumentsByRecency,
  type DocumentStatusFilter,
} from './documentUtils'
import type { DocumentsListStatus, UploadSuccessSummary } from './types'

interface Props {
  /** Called after the user clicks "Usar no chat" on a card. */
  onScopeSelected?: () => void
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Top-level documents workspace. Pulls the catalog scoped to the authenticated
 * organization's tenant and lets the user upload + select scope without
 * leaving the chat shell.
 *
 * Step 7.9C: also owns the delete flow — opens a confirm modal, fires the
 * DELETE call, and on success refreshes both the local list and the catalog
 * cached in ScopeProvider so the chat scope selector reflects the change
 * immediately. If the deleted document was the active retrieval scope, the
 * provider's reload() already drops the selection because the scope no
 * longer matches any catalog row.
 */
export function DocumentsPage({ onScopeSelected }: Props) {
  const { organization, logout } = useAuth()
  const tenantId = organization?.tenant_id ?? null
  const orgLabel = organization?.display_name ?? organization?.slug ?? '—'

  const { selectDocument, clearScope, selected: activeScope, reload: reloadScope } = useScope()

  const [documents, setDocuments] = useState<CatalogDocumentDTO[]>([])
  const [status, setStatus] = useState<DocumentsListStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [recentUpload, setRecentUpload] = useState<UploadSuccessSummary | null>(null)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<DocumentStatusFilter>('all')

  // Step 7.9C — delete confirmation + in-flight state.
  const [pendingDelete, setPendingDelete] = useState<CatalogDocumentDTO | null>(null)
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteSuccess, setDeleteSuccess] = useState<{ title: string } | null>(null)

  const loadDocuments = useCallback(async () => {
    if (!tenantId) return
    setStatus('loading')
    setError(null)
    try {
      // Step 8.0 — a Biblioteca de Documentos é dedicada a documentos
      // de caso/cliente/paciente. Reference documents ficam isolados na
      // Biblioteca de Referência para evitar mistura de escopo.
      const response = await documentsApi.listDocuments({
        tenantId,
        documentKind: 'case_document',
      })
      setDocuments(sortDocumentsByRecency(response.items))
      setStatus('ready')
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await logout()
        return
      }
      setError(errorDetail(err, 'Falha ao carregar a biblioteca.'))
      setStatus('error')
    }
  }, [tenantId, logout])

  useEffect(() => {
    if (!tenantId) return
    void loadDocuments()
  }, [tenantId, loadDocuments])

  /**
   * Refresh both the local list AND the catalog cached in ScopeProvider in
   * sequence, so the success pill in the page header always lines up with
   * the row that just appeared and the chat scope selector can immediately
   * select the newly indexed document. Errors are swallowed deliberately —
   * the upload itself already succeeded; a refresh hiccup is non-fatal and
   * gets reported through the normal list error state.
   */
  const handleUploaded = useCallback(
    async (summary: UploadSuccessSummary) => {
      setRecentUpload(summary)
      await loadDocuments()
      await reloadScope()
    },
    [loadDocuments, reloadScope],
  )

  /**
   * Phase 3B — a queued job just became `indexed` in the background. Refresh
   * the catalog + the scope cache so the freshly-indexed document shows up in
   * the list and can be picked as a chat scope, with no page reload. Errors are
   * swallowed (the same non-fatal stance as handleUploaded).
   */
  const handleJobIndexed = useCallback(async () => {
    await loadDocuments()
    await reloadScope()
  }, [loadDocuments, reloadScope])

  const handleUseAsScope = useCallback(
    (doc: CatalogDocumentDTO) => {
      selectDocument(doc)
      onScopeSelected?.()
    },
    [selectDocument, onScopeSelected],
  )

  const handleRequestDelete = useCallback((doc: CatalogDocumentDTO) => {
    setDeleteError(null)
    setDeleteSuccess(null)
    setPendingDelete(doc)
  }, [])

  const handleCancelDelete = useCallback(() => {
    if (deletingDocumentId) return
    setPendingDelete(null)
  }, [deletingDocumentId])

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete || !tenantId) return
    if (deletingDocumentId) return // prevent double-submit
    const target = pendingDelete
    setDeletingDocumentId(target.document_id)
    setDeleteError(null)
    try {
      await documentsApi.deleteDocument({
        tenantId,
        documentId: target.document_id,
      })

      // Step 7.9C-FIX-1: only commit UI state changes AFTER the backend
      // confirmed the delete. If the call throws, the card stays visible
      // and the modal surfaces a readable error.

      // If the deleted document was the active retrieval scope, drop it
      // BEFORE refetching the catalog so the chat shell never renders a
      // dangling scope_document_id even for a single frame.
      const targetScope = scopeFromDocument(target)
      if (scopeEquals(activeScope, targetScope)) {
        clearScope()
      }

      // Local removal — the reload below is the source of truth, but
      // updating locally avoids a noticeable flicker when the catalog
      // endpoint takes a moment.
      setDocuments((prev) =>
        prev.filter((d) => d.document_id !== target.document_id),
      )
      setDeleteSuccess({ title: documentTitle(target) })
      setPendingDelete(null)

      // Refresh both views. Swallow errors here — the delete itself succeeded;
      // surface refresh failures via the normal list-error state below.
      await loadDocuments()
      await reloadScope()
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await logout()
        return
      }
      // Backend rejected the delete: stop loading, surface the readable
      // message in the page, and close the modal so the user can see the
      // error and try again by clicking trash on the card.
      setDeleteError(errorDetail(err, 'Falha ao excluir o documento.'))
      setPendingDelete(null)
    } finally {
      setDeletingDocumentId(null)
    }
  }, [
    pendingDelete,
    tenantId,
    deletingDocumentId,
    activeScope,
    clearScope,
    loadDocuments,
    reloadScope,
    logout,
  ])

  const filteredDocuments = useMemo(
    () => filterDocuments(documents, query, statusFilter),
    [documents, query, statusFilter],
  )

  if (!tenantId) {
    return (
      <div className="doc-workspace">
        <div className="doc-state doc-state-error">
          <div className="doc-state-icon"><Icon name="close" /></div>
          <h3>Sessão sem organização</h3>
          <p>Faça login novamente para acessar a biblioteca.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="doc-workspace" data-testid="documents-page">
      <header className="doc-workspace-head">
        <div>
          <h1 className="doc-workspace-title">
            <Icon name="doc" /> Biblioteca de documentos
          </h1>
          <p className="doc-workspace-sub">
            Documentos indexados para <strong>{orgLabel}</strong>. Faça upload de novos
            arquivos e selecione-os como escopo do chat.
          </p>
          <p className="doc-workspace-hint">
            Esta versão usa um documento/escopo por consulta. Seleção múltipla
            será adicionada em uma etapa futura.
          </p>
        </div>
        <button
          type="button"
          className="doc-workspace-refresh"
          onClick={() => void loadDocuments()}
          disabled={status === 'loading'}
          title="Recarregar lista"
        >
          <Icon name="rotate" />
          {status === 'loading' ? 'Carregando…' : 'Atualizar'}
        </button>
      </header>

      <div className="doc-workspace-body">
        <section className="doc-workspace-list" aria-labelledby="doc-list-title">
          <div className="doc-workspace-section-head">
            <h2 id="doc-list-title">
              Documentos
              <span className="doc-list-count" title="Documentos exibidos / total">
                {filteredDocuments.length === documents.length
                  ? documents.length
                  : `${filteredDocuments.length} / ${documents.length}`}
              </span>
            </h2>
            {deleteSuccess ? (
              <div className="doc-recent-pill is-success" title={deleteSuccess.title}>
                <Icon name="trash" />
                Excluído: {deleteSuccess.title}
              </div>
            ) : recentUpload && status === 'ready' ? (
              <div className="doc-recent-pill" title={recentUpload.document_id}>
                <Icon name="sparkles" />
                {recentUpload.status === 'queued'
                  ? 'Na fila'
                  : recentUpload.replaced
                    ? 'Reindexado'
                    : 'Novo'}
                : {recentUpload.document_id}
              </div>
            ) : null}
          </div>
          {deleteError ? (
            <div className="doc-state doc-state-error" role="alert">
              <div className="doc-state-icon"><Icon name="close" /></div>
              <p>{deleteError}</p>
            </div>
          ) : null}
          <DocumentList
            documents={filteredDocuments}
            totalCount={documents.length}
            status={status}
            error={error}
            query={query}
            statusFilter={statusFilter}
            onQueryChange={setQuery}
            onStatusFilterChange={setStatusFilter}
            onRetry={() => void loadDocuments()}
            onUseAsScope={handleUseAsScope}
            onRequestDelete={handleRequestDelete}
            deletingDocumentId={deletingDocumentId}
          />
        </section>

        <aside className="doc-workspace-side">
          <DocumentUploadPanel
            tenantId={tenantId}
            organizationLabel={orgLabel}
            existingDocuments={documents}
            onUploaded={handleUploaded}
          />
          <IngestionJobsPanel
            tenantId={tenantId}
            refreshToken={recentUpload?.document_id ?? null}
            onIndexed={() => void handleJobIndexed()}
          />
        </aside>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Excluir documento?"
        message={
          pendingDelete
            ? buildDeleteMessage(pendingDelete, activeScope)
            : ''
        }
        confirmLabel={
          deletingDocumentId ? 'Excluindo…' : 'Excluir documento'
        }
        cancelLabel="Cancelar"
        destructive
        busy={deletingDocumentId !== null}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={handleCancelDelete}
      />
    </div>
  )
}

function buildDeleteMessage(
  doc: CatalogDocumentDTO,
  activeScope: ReturnType<typeof useScope>['selected'],
): string {
  const title = documentTitle(doc)
  const chunkLabel = doc.chunk_count === 1 ? '1 chunk' : `${doc.chunk_count} chunks`
  const isActive = scopeEquals(activeScope, scopeFromDocument(doc))
  const activeWarning = isActive
    ? ' O escopo ativo do chat voltará para "Todo o workspace".'
    : ''
  return (
    `Isso removerá "${title}" (document_id: ${doc.document_id}) e os ` +
    `${chunkLabel} indexados usados pelo RAG. Essa ação não pode ser desfeita.` +
    activeWarning
  )
}
